import React, { useState, useEffect } from 'react';
import { Badge, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { db } from '../db';
import { formatDateTime } from '../dateFormat';

const STALE_THRESHOLD_DAYS = 7;

const OfflineMapStatus: React.FC = () => {
  const [status, setStatus] = useState<'cached' | 'stale' | 'none'>('none');
  const [lastSync, setLastSync] = useState<Date | null>(null);

  useEffect(() => {
    const checkStatus = async () => {
      const orchards = await db.orchards.toArray();
      if (orchards.length === 0) {
        setStatus('none');
        setLastSync(null);
        return;
      }

      // Find the oldest lastUpdated timestamp to be conservative
      const timestamps = orchards
        .map(o => o.lastUpdated ? new Date(o.lastUpdated).getTime() : 0)
        .filter(t => t > 0);
      
      if (timestamps.length === 0) {
        setStatus('none');
        setLastSync(null);
        return;
      }

      const minTimestamp = Math.min(...timestamps);
      const oldestSync = new Date(minTimestamp);
      setLastSync(oldestSync);

      const daysOld = (new Date().getTime() - minTimestamp) / (1000 * 60 * 60 * 24);
      
      if (daysOld > STALE_THRESHOLD_DAYS) {
        setStatus('stale');
      } else {
        setStatus('cached');
      }
    };

    checkStatus();
    const interval = setInterval(checkStatus, 60000); // Check every minute

    return () => clearInterval(interval);
  }, []);

  if (status === 'none') return null;

  const renderTooltip = (props: any) => (
    <Tooltip id="map-status-tooltip" {...props}>
      {status === 'cached'
        ? `Orchard data cached on ${lastSync ? formatDateTime(lastSync) : ''}`
        : `Orchard data is older than ${STALE_THRESHOLD_DAYS} days. Please connect to internet to refresh.`
      }
    </Tooltip>
  );

  return (
    <OverlayTrigger
      placement="bottom"
      delay={{ show: 250, hide: 400 }}
      overlay={renderTooltip}
    >
      <Badge 
        bg={status === 'cached' ? 'info' : 'warning'} 
        text={status === 'cached' ? 'white' : 'dark'}
        className="ms-2"
        style={{ cursor: 'help' }}
      >
        <i className={`bi ${status === 'cached' ? 'bi-map-fill' : 'bi-exclamation-triangle-fill'} me-1`}></i>
        {status === 'cached' ? 'Map Data Cached' : 'Map Data Stale'}
      </Badge>
    </OverlayTrigger>
  );
};

export default OfflineMapStatus;
